import {useContext, useEffect, useRef, useState, useCallback} from 'react';
import {Box, Button, Stack, Typography, useMediaQuery} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import {useNavigate} from 'react-router-dom'; // Add this import
import {useClientApi} from '../../../../api/client';
import {getClientConfig} from '../../../../config/clientThemes';
import mainLogo from '../../../../images/black_letters_with_blue_shadow.svg';
import ShareButtons from '../../../../components/buttons/ShareButtons';
import {CATEGORY_MAP} from '../../../Results/Components/analysis/parseRecoProducts';
import HealthCategoryNav from '../../../../components/NavigationBar/HealthCategoryNavigationBar';
import {useProductsApi} from '../../../../api/products';
import APIObjectsContext from '../../../../context/APIObjectsContext';
import useGeoLocation from 'react-ipgeolocation';
import {identifyClientType} from '../../../../utils/identifyClientType';
import {CLIENT_TYPES} from '../../../../api/clientconfig';

const ShowAllProductsPage = ({
  sortedMetricsByHealthOrder,
  blurAndHide,
  handleNavigation,
  onBackBtnClick,
  getCategories,
  activeCategoryIndex,
  handleCategorySelect,
  item,
  config = {}, // Add config prop
  organization = {}, // Add organization prop
  transformedHealthMetrics = {}, // Add this prop to get full transformed data
}) => {
  const navigate = useNavigate(); // Add useNavigate hook
  const cardRef = useRef(null);
  const productsApi = useProductsApi();
  const getUserCountryLocation = useGeoLocation();
  const {userAssessmentID, setUserAssessmentID} = useContext(APIObjectsContext);

  // State declarations
  const [userCountryLocation, setUserCountryLocation] = useState('');
  const [ShadowClass, setShadowClass] = useState(true);
  const [clientType, setClientType] = useState(CLIENT_TYPES.ATP);
  const [orgProductsList, setOrgProductsList] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Media queries
  const isSmallScreen = useMediaQuery('(max-width:499px)');
  const isGalaxyFold = useMediaQuery('(max-width:280px)');
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));

  // Use passed config or fallback to detected client type
  const clientConfig = config || getClientConfig(clientType);
  const clientApi = useClientApi(clientType);

  // Get categories as array (fix for nav not showing)
  const categories = typeof getCategories === 'function' ? getCategories() : [];
  console.log('Categories:', categories, 'Type:', typeof categories, 'Is Array:', Array.isArray(categories));

  const cssToBlurAndHideCard = {
    filter: blurAndHide ? 'blur(10px)' : 'blur(0px)',
    pointerEvents: blurAndHide ? 'none' : 'auto',
  };

  // Memoize the getTop3Products function to prevent recreating it on every render
  const getTop3Products = useCallback(
    prodRes => {
      console.log('getTop3Products called with:', prodRes);

      let symptomRelatedProducts = [];
      if (prodRes.data && prodRes.data.symptoms) {
        symptomRelatedProducts = prodRes.data.symptoms;
      }

      const recommendedProducts = [];
      const seenProductIds = new Set();

      // Add symptom-related products first
      if (Array.isArray(symptomRelatedProducts)) {
        symptomRelatedProducts.forEach(product => {
          if (!seenProductIds.has(product.product_id)) {
            recommendedProducts.push(product);
            seenProductIds.add(product.product_id);
          }
        });
      }

      // Process categories in sortedMetricsByHealthOrder with safety checks
      if (Array.isArray(sortedMetricsByHealthOrder)) {
        sortedMetricsByHealthOrder.forEach(item => {
          if (item?.category) {
            const categories = CATEGORY_MAP[item?.category];

            if (Array.isArray(categories)) {
              categories.forEach(category => {
                if (prodRes.data && prodRes.data.hasOwnProperty(category)) {
                  const categoryProducts = prodRes.data[category];
                  if (Array.isArray(categoryProducts)) {
                    categoryProducts.forEach(product => {
                      if (!seenProductIds.has(product.product_id)) {
                        recommendedProducts.push(product);
                        seenProductIds.add(product.product_id);
                      }
                    });
                  }
                }
              });
            }
          }
        });
      } else {
        console.warn('sortedMetricsByHealthOrder is not an array:', sortedMetricsByHealthOrder);
      }

      const allRecommendedProducts = [...recommendedProducts];

      // Add products from 'products' key if needed
      if (allRecommendedProducts.length < 3 && prodRes.data && prodRes.data.hasOwnProperty('products')) {
        const additionalProducts = prodRes.data.products;
        if (Array.isArray(additionalProducts)) {
          additionalProducts.forEach(product => {
            if (!seenProductIds.has(product.product_id) && allRecommendedProducts.length < 3) {
              allRecommendedProducts.push(product);
              seenProductIds.add(product.product_id);
            }
          });
        }
      }

      const top3Products = allRecommendedProducts.slice(0, 3);
      console.log('Top 3 Recommended Products:', top3Products);
      return top3Products;
    },
    [sortedMetricsByHealthOrder],
  );

  // Memoize handleRedirect to prevent recreation
  const handleRedirect = useCallback(link => {
    if (link) {
      window.open(link, '_blank');
    }
  }, []);

  // Enhanced Learn More navigation - navigate to Physical Health analysis with full transformed data
  const handleLearnMoreNavigation = useCallback(() => {
    console.log('Learn More clicked from ShowAllProductsPage');
    console.log('transformedHealthMetrics:', transformedHealthMetrics);
    console.log('sortedMetricsByHealthOrder:', sortedMetricsByHealthOrder);

    // First try to use the full transformed health metrics
    if (transformedHealthMetrics?.physicalHealth) {
      const physicalHealthItem = transformedHealthMetrics.physicalHealth;
      console.log('Using transformed Physical Health data:', physicalHealthItem);

      // Create analysis item with full transformed data
      const analysisItem = {
        ...physicalHealthItem, // This contains name, value, rating, results, etc.
        isGameCompleted: true,
        userAssessmentID: localStorage.getItem('session-id') || 'default',
        cardKey: 'physicalHealth',
        category: physicalHealthItem.name,
        healthName: physicalHealthItem.name,
        fieldValue: physicalHealthItem.value,
        healthOrderNo: physicalHealthItem.value,
      };

      console.log('Final analysis item with transformed data:', analysisItem);

      navigate('/results/analysis', {
        state: {
          item: analysisItem,
        },
      });
      return;
    }

    console.log('Transformed data not available, using fallback...');

    // Fallback to sortedMetricsByHealthOrder
    const physicalHealthItem = sortedMetricsByHealthOrder?.find(
      item =>
        item.healthName === 'Physical Health' ||
        item.category === 'Physical Health' ||
        item.cardKey === 'physicalHealth' ||
        item.healthName?.toLowerCase().includes('physical'),
    );

    console.log('Found Physical Health item from sorted metrics:', physicalHealthItem);

    if (physicalHealthItem) {
      // Try to get full transformed data for this item
      const fullTransformedItem = transformedHealthMetrics?.[physicalHealthItem.cardKey];
      console.log('Full transformed item for cardKey:', physicalHealthItem.cardKey, fullTransformedItem);

      const analysisItem = {
        name: physicalHealthItem.healthName || physicalHealthItem.category,
        value: physicalHealthItem.fieldValue,
        rating: physicalHealthItem.fieldValue,
        cardKey: physicalHealthItem.cardKey,
        category: physicalHealthItem.category,
        healthName: physicalHealthItem.healthName,
        fieldValue: physicalHealthItem.fieldValue,
        healthOrderNo: physicalHealthItem.healthOrderNo,
        isGameCompleted: true,
        userAssessmentID: localStorage.getItem('session-id') || 'default',
        // Use transformed results if available, otherwise empty array
        results: fullTransformedItem?.results || [],
        resultHeading: fullTransformedItem?.resultHeading || `Your ${physicalHealthItem.healthName} Metrics`,
      };

      console.log('Final analysis item with fallback data:', analysisItem);

      navigate('/results/analysis', {
        state: {
          item: analysisItem,
        },
      });
    } else {
      console.log('Physical Health not found, using first available item');
      // Fallback: use the first item in sortedMetricsByHealthOrder
      const firstItem = sortedMetricsByHealthOrder?.[0];
      if (firstItem) {
        const fullTransformedItem = transformedHealthMetrics?.[firstItem.cardKey];
        console.log('Using first item:', firstItem, 'with transformed data:', fullTransformedItem);

        const analysisItem = {
          name: firstItem.healthName || firstItem.category,
          value: firstItem.fieldValue,
          rating: firstItem.fieldValue,
          cardKey: firstItem.cardKey,
          category: firstItem.category,
          healthName: firstItem.healthName,
          fieldValue: firstItem.fieldValue,
          healthOrderNo: firstItem.healthOrderNo,
          isGameCompleted: true,
          userAssessmentID: localStorage.getItem('session-id') || 'default',
          results: fullTransformedItem?.results || [],
          resultHeading: fullTransformedItem?.resultHeading || `Your ${firstItem.healthName} Metrics`,
        };

        console.log('Final analysis item with first item:', analysisItem);

        navigate('/results/analysis', {
          state: {
            item: analysisItem,
          },
        });
      } else {
        console.log('No items available, calling original handler');
        // Final fallback: call the original handler if provided
        if (typeof handleNavigation === 'function') {
          handleNavigation();
        }
      }
    }
  }, [transformedHealthMetrics, sortedMetricsByHealthOrder, navigate, handleNavigation]);

  // Set client type once on mount - use dynamic detection or passed organization
  useEffect(() => {
    if (organization?.type) {
      // Use organization type if available
      setClientType(organization.type);
    } else {
      // Fallback to detection
      setClientType(identifyClientType());
    }
  }, [organization?.type]);

  // Handle geolocation with proper dependency management
  useEffect(() => {
    console.log(getUserCountryLocation, userCountryLocation);

    if (getUserCountryLocation.error) {
      setUserCountryLocation(null);
    } else if (getUserCountryLocation.country && !userCountryLocation) {
      setUserCountryLocation(getUserCountryLocation.country);
    }
  }, [getUserCountryLocation.country, getUserCountryLocation.error]);

  // Main data fetching effect with proper conditions
  useEffect(() => {
    // Prevent multiple simultaneous calls
    if (loading || hasInitialized) {
      return;
    }

    const fetchTopProducts = async () => {
      let response;
      const sessionId = localStorage.getItem('session-id');

      try {
        setLoading(true);
        setHasInitialized(true);

        if (clientType === CLIENT_TYPES.DEFAULT && (userCountryLocation !== '' || userCountryLocation === null)) {
          // Create product recommendation for the user
          await productsApi.createProductRecommendation(userAssessmentID, userCountryLocation);
          // Fetch the recommended products for aimd flow
          response = await productsApi.getRecommendedProducts(userAssessmentID);
        } else if (sessionId) {
          response = await clientApi.getProductRecommendations(sessionId);
        }

        // If no response at all just return
        if (response === undefined) {
          setLoading(false);
          return;
        }

        // Only process if the response is successful
        if (response?.status === 200 && response?.data) {
          console.log(response.data);
          const topProducts = getTop3Products(response);

          // Format the top 3 products
          const formattedTopProducts = topProducts.map(product => ({
            id: product.product_id || Math.random(),
            name: product.product_name || product?.name,
            description: product.product_description || product?.reason,
            restrictions: product.product_restrictions,
            amazonAffiliateLink: product.product_link,
            productImageAddress: product.product_image_address,
            brand: product.product_brand,
            reason: product.reason,
            vitalName: product.vital_name || null,
            vitalValue: product.vital_value || null,
            brandImageAddress: product.brand_image_address,
          }));

          setOrgProductsList(formattedTopProducts);
          setError(null);
        } else {
          setError('Error fetching products');
        }
      } catch (error) {
        console.error('Error fetching top products:', error);
        setError('Error fetching products');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we have the necessary data and haven't already initialized
    if (
      (clientType === CLIENT_TYPES.DEFAULT && (userCountryLocation !== '' || userCountryLocation === null)) ||
      (clientType !== CLIENT_TYPES.DEFAULT && localStorage.getItem('session-id'))
    ) {
      fetchTopProducts();
    }
  }, [
    userCountryLocation,
    clientType,
    userAssessmentID,
    productsApi,
    clientApi,
    getTop3Products,
    loading,
    hasInitialized,
  ]);

  // Reset initialization flag when critical props change
  useEffect(() => {
    setHasInitialized(false);
  }, [userAssessmentID]);

  // Debug logging
  useEffect(() => {
    console.log('ShowAllProductsPage props:', {
      sortedMetricsByHealthOrder: {
        value: sortedMetricsByHealthOrder,
        type: typeof sortedMetricsByHealthOrder,
        isArray: Array.isArray(sortedMetricsByHealthOrder),
        length: sortedMetricsByHealthOrder?.length,
      },
      transformedHealthMetrics: {
        value: transformedHealthMetrics,
        physicalHealth: transformedHealthMetrics?.physicalHealth,
      },
      handleNavigation: typeof handleNavigation,
      config,
      organization,
    });
  }, [sortedMetricsByHealthOrder, transformedHealthMetrics, handleNavigation, config, organization]);

  // Early return with error boundary
  if (!Array.isArray(sortedMetricsByHealthOrder)) {
    console.warn('sortedMetricsByHealthOrder is not an array, rendering with empty state');
    return (
      <Box sx={{padding: '20px', textAlign: 'center'}}>
        <Typography>Loading products...</Typography>
      </Box>
    );
  }

  return isLargeScreen ? (
    <Stack className="result-page-card" padding={3}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
        className="results-page-wrapper"
      >
        <Stack className="result-page-card large-card" padding={isGalaxyFold ? 1 : 2}>
          <Stack className="large-result-component">
            <Stack
              sx={{
                minHeight: '380px !important',
              }}
              className={ShadowClass ? 'result-component card-gradient large' : 'result-component large'}
            >
              {orgProductsList && Array.isArray(orgProductsList) && orgProductsList.length ? (
                orgProductsList.slice(0, 3).map((data, index) => (
                  <Box
                    key={data?.id || index}
                    sx={{
                      borderRadius: '15px',
                      padding: '4px 16px',
                      ...cssToBlurAndHideCard,
                    }}
                  >
                    <Stack display="flex" flexDirection="row" justifyContent="space-between" gap={1}>
                      <Stack flex="6">
                        <Typography
                          sx={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#000000',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: 1,
                          }}
                        >
                          {data?.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#000000',
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            WebkitLineClamp: 2,
                          }}
                        >
                          {data?.reason}
                        </Typography>
                        <Button
                          sx={{
                            color: clientType === CLIENT_TYPES.DEFAULT ? '#72D8DA' : '#F15C30',
                            padding: '4px',
                            display: 'flex',
                            justifyContent: 'start',
                            padding: '2px',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              background: 'transparent',
                            },
                          }}
                          onClick={() => handleRedirect(data?.amazonAffiliateLink)}
                        >
                          <Typography
                            sx={{
                              fontSize: '20px',
                              backgroundColor: clientType === CLIENT_TYPES.DEFAULT ? '#72D8DA' : '#F15C30',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: '6px',
                              width: '28px',
                              height: '28px',
                            }}
                          >
                            <AddShoppingCartIcon sx={{color: 'white', fontSize: '18px'}} />
                          </Typography>
                          View Product
                        </Button>
                      </Stack>
                      <Stack display="flex" justifyContent="center" alignItems="center" flex="3">
                        <Box>
                          <img
                            src={data?.productImageAddress}
                            style={{width: '50px', height: 'auto'}}
                            alt={data?.name || 'Product'}
                          />
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>
                ))
              ) : (
                <Box sx={{height: '150px', textAlign: 'center', padding: '20px 0px'}}>
                  {loading ? (
                    <Typography color="grey" fontSize="16px" marginY={2}>
                      Loading...
                    </Typography>
                  ) : (
                    <Typography>No Product Data Found</Typography>
                  )}
                </Box>
              )}
              <Box className="result-logo" sx={{marginBottom: '0px', paddingTop: '4px'}}>
                {clientType === CLIENT_TYPES.ATP ? (
                  <a href="https://atplab.com/?rfsn=8347543.b2a03c" target="_blank" rel="noopener noreferrer">
                    <img src={config.logos?.main} height={config.logos?.height || 40} alt="Logo" />
                  </a>
                ) : (
                  <img src={config?.logos?.main} height={config?.logos?.height} className="" />
                )}
                {clientType !== CLIENT_TYPES.DEFAULT && (
                  <Typography sx={{fontSize: '14px'}}>
                    Your health scan was powered by
                    <img src={mainLogo} className="custom-main-logo-for-md" alt="Main Logo" />
                  </Typography>
                )}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  ) : (
    <Box
      sx={{
        backgroundColor: '#FFF',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '90%',
        padding: isLargeScreen ? '20px' : isSmallScreen ? '4px 20px' : '10px 20px',
      }}
      className="results-page-wrapper"
    >
      <Stack className="result-page-card" sx={{backgroundColor: '#fff'}}>
        <Box style={{textDecoration: 'none', width: '100%', display: 'flex', justifyContent: 'space-between'}}>
          <Stack onClick={onBackBtnClick} display="flex" flexDirection="row" style={{cursor: 'pointer'}}>
            {clientConfig?.backButton}
          </Stack>
        </Box>
        <Stack>
          <Stack>
            <Typography
              fontSize={isSmallScreen ? '14' : '18px'}
              fontWeight="700"
              color="#545454"
              textAlign="center"
              marginTop={isSmallScreen ? '2px' : '4px'}
              lineHeight={isSmallScreen ? '16px' : '30px'}
              marginBottom="4px"
            >
              Top Products for You
            </Typography>
            <Typography
              sx={{
                color: '#545454',
                fontFamily: 'Poppins',
                fontSize: isSmallScreen ? '12px' : '14px',
                fontWeight: 600,
                lineHeight: isSmallScreen ? 1.2 : 1.5,
                textAlign: 'center',
              }}
            >
              Our recommendations to elevate your health in your weakest health categories.
            </Typography>
          </Stack>

          <Stack
            ref={cardRef}
            sx={{position: 'relative', bottom: '25px'}}
            className={`${ShadowClass ? 'result-component card-gradient' : 'result-component'} result-component-org`}
          >
            {orgProductsList && Array.isArray(orgProductsList) && orgProductsList.length ? (
              orgProductsList.slice(0, 3).map((data, index) => (
                <Box
                  key={data?.id || index}
                  sx={{
                    borderRadius: '15px',
                    padding: isSmallScreen ? '1px 12px 5px 12px' : '4px 14px',
                    ...cssToBlurAndHideCard,
                  }}
                >
                  <Stack display="flex" flexDirection="row" justifyContent="space-between" gap={1}>
                    <Stack flex="6">
                      <Typography
                        sx={{
                          fontSize: isSmallScreen ? '13px' : '16px',
                          fontWeight: 600,
                          color: '#000000',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          WebkitLineClamp: 1,
                        }}
                      >
                        {data?.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: isSmallScreen ? '11px' : '14px',
                          fontWeight: 500,
                          color: '#000000',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: isSmallScreen ? 1.2 : 1.3,
                          WebkitLineClamp: 2,
                        }}
                      >
                        {data?.reason}
                      </Typography>
                      <Button
                        onClick={() => handleRedirect(data?.amazonAffiliateLink)}
                        sx={{
                          color: clientType === CLIENT_TYPES.DEFAULT ? '#72D8DA' : '#F15C30',
                          fontSize: isSmallScreen ? '12px' : '15px',
                          display: 'flex',
                          justifyContent: 'start',
                          padding: '4px 4px 4px 0px',
                          '&:hover': {
                            backgroundColor: 'none',
                            background: 'transparent',
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '20px',
                            backgroundColor: clientType === CLIENT_TYPES.DEFAULT ? '#72D8DA' : '#F15C30',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '6px',
                            width: isSmallScreen ? '22px' : '28px',
                            height: isSmallScreen ? '22px' : '28px',
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          <AddShoppingCartIcon sx={{color: 'white', fontSize: isSmallScreen ? '14px' : '18px'}} />
                        </Typography>
                        View Product
                      </Button>
                    </Stack>
                    <Stack display="flex" justifyContent="center" alignItems="center" flex="3">
                      <Box>
                        <img
                          src={data?.productImageAddress}
                          style={{width: isSmallScreen ? '40px' : '50px', height: 'auto'}}
                          alt={data?.name || 'Product'}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                </Box>
              ))
            ) : (
              <Box sx={{height: '150px', textAlign: 'center', padding: '20px 0px'}}>
                {loading ? (
                  <Typography color="grey" fontSize="14px" marginY={2}>
                    Loading...
                  </Typography>
                ) : (
                  <Typography> No Product Data Found </Typography>
                )}
              </Box>
            )}
            <Box className="result-logo" sx={{bottom: '6px !important', paddingTop: '4px !important'}}>
              {clientType === CLIENT_TYPES.ATP ? (
                <a href="https://atplab.com/?rfsn=8347543.b2a03c" target="_blank" rel="noopener noreferrer">
                  <img src={config.logos?.main} height={config.logos?.height || 40} alt="Logo" />
                </a>
              ) : (
                <img src={config?.logos?.main} height={config?.logos?.height} className="" />
              )}
              {clientType !== CLIENT_TYPES.DEFAULT && (
                <Typography
                  sx={{
                    fontSize: isGalaxyFold ? '8px' : '10px',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    paddingTop: '8px',
                    justifyContent: 'center',
                  }}
                >
                  Your health scan was powered by
                  <img src={mainLogo} className="custom-main-logo" alt="Main Logo" />
                </Typography>
              )}
            </Box>
          </Stack>
        </Stack>

        {/* Health Category Navigation - FIXED */}
        {categories.length > 0 && (
          <Box sx={{position: 'relative', bottom: '25px'}}>
            <HealthCategoryNav
              categories={categories} // Use categories array instead of function
              activeIndex={activeCategoryIndex}
              onSelectCategory={handleCategorySelect}
              isLargeScreen={false}
              clientBaseNavCss={{
                arrowColor: clientConfig?.resultPageSlider?.arrowColor,
                arrowHOverColor: clientConfig?.resultPageSlider?.arrowHOverColor,
                movingIndicatorColor: clientConfig?.resultPageSlider?.movingIndicatorColor,
                startIconColorActive: clientConfig?.resultPageSlider?.startIconColorActive,
              }}
            />
          </Box>
        )}

        {/* Disclaimer Text - ADDED */}
        <Box sx={{position: 'relative', bottom: '25px', mt: 2}}>
          <Typography
            color="black"
            variant="p"
            component="p"
            fontWeight="400"
            sx={{
              fontSize: isGalaxyFold ? '10px' : isSmallScreen ? '10px' : '12px',
              textAlign: 'center',
              px: 2,
            }}
          >
            These numbers are only estimates and are not a substitute for the judgment of a healthcare professional.
            They are intended to improve awareness and general wellness.
          </Typography>
        </Box>

        <Box sx={{position: 'relative', bottom: '15px'}}>
          <Button
            fullWidth
            onClick={handleLearnMoreNavigation}
            className={'btn-shadow'}
            sx={{
              height: isSmallScreen ? '50px' : '60px',
              borderRadius: '10px',
              marginBottom: isSmallScreen ? '6px' : '10px',
              background: clientConfig.button?.background,
              maxWidth: '400px',
              textAlign: 'center',
              color: '#fff',
              '&:hover': {
                color: 'black',
              },
            }}
          >
            <Typography
              className="btn-txt"
              variant="p"
              component="p"
              fontWeight="600"
              sx={{
                fontSize: isSmallScreen ? '16px' : '20px',
              }}
            >
              Learn More
            </Typography>
          </Button>

          <ShareButtons socialButtonsCss={clientConfig?.socialButtons} cardRef={cardRef} />
        </Box>
      </Stack>
    </Box>
  );
};

export default ShowAllProductsPage;
